import { useMemo } from 'react';

import {
  CHART_COLORS_PALETTE,
  generateChartColors,
} from 'src/utils/chartColors';

/**
 * Hook customizado para gerar cores únicas para gráficos
 * Memoiza o resultado para evitar recálculos desnecessários
 *
 * @param baseColor - Cor base em formato hexadecimal (padrão: '#FF8211')
 * @param count - Quantidade de cores necessárias
 * @param usePalette - Se true, usa a paleta pré-definida; se false, gera cores dinamicamente
 * @returns Array de cores em formato hexadecimal
 */
export const useChartColors = (
  baseColor = '#FF8211',
  count: number,
  usePalette = false,
): string[] => {
  return useMemo(() => {
    if (count <= 0) return [];

    if (usePalette) {
      // Usa a paleta pré-definida, repetindo se necessário
      const colors: string[] = [];
      for (let i = 0; i < count; i++) {
        colors.push(CHART_COLORS_PALETTE[i % CHART_COLORS_PALETTE.length]);
      }
      return colors;
    }

    // Gera cores dinamicamente baseado na cor base
    return generateChartColors(baseColor, count);
  }, [baseColor, count, usePalette]);
};

/**
 * Hook para obter cores específicas para gráficos de composição
 * Otimizado para o caso de uso específico dos componentes de composição
 *
 * @param compositions - Array de composições
 * @param baseColor - Cor base (padrão: '#FF8211')
 * @returns Array de cores correspondente às composições
 */
export const useCompositionColors = (
  compositions: Array<{ _key: string; color?: string }> = [],
  baseColor = '#FF8211',
): string[] => {
  return useMemo(() => {
    if (!compositions || compositions.length === 0) return [];

    // Gera cores para todas as composições
    const generatedColors = generateChartColors(baseColor, compositions.length);

    // Combina cores customizadas com cores geradas
    const result: string[] = [];

    compositions.forEach((comp, index) => {
      if (comp.color) {
        result.push(comp.color);
      } else {
        result.push(generatedColors[index] || baseColor);
      }
    });

    return result;
  }, [compositions, baseColor]);
};
